import { DocumentNode, gql } from '@apollo/client';
import {
  Hide,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Td,
  Text,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import * as React from 'react';

import { DeleteLibraryResourceModal } from '@/components/LibraryResourceModals/DeleteLibraryResourceModal';
import { RenameLibraryResourceModal } from '@/components/LibraryResourceModals/RenameLibraryResourceModal';
import {
  Edit2Icon,
  FolderIcon,
  MoreHorizontalIcon,
  MoveIcon,
  TrashIcon,
} from '@/imports/ui/chakra/feather';
import { MoveLibraryFolderModal } from '@/imports/ui/components/resource/MoveLibraryFolderModal';
import { ResourceListDragHandle } from '@/imports/ui/components/resource/ResourceListDragHandle';
import {
  ResourceListName,
  resourceListDateTdProps,
  resourceListNameTdProps,
  resourceListNameTextProps,
} from '@/imports/ui/components/resource/ResourceListName';
import {
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';

import { ResourceListFolder_LibraryFolderFragment } from './graphql-types/ResourceListFolder_LibraryFolderFragment';

const FolderItemDropdown: React.FC<{
  isDeletable: boolean;
  onClickMoveFolder: () => void;
  onClickDeleteFolder: () => void;
  onClickEdit: () => void;
}> = ({ isDeletable, onClickEdit, onClickMoveFolder, onClickDeleteFolder }) => {
  return (
    <Menu aria-label="folder actions">
      <MenuButton
        as={IconButton}
        variant="outline"
        colorScheme="gray"
        icon={<MoreHorizontalIcon />}
        size="sm"
      />
      <Portal>
        <MenuList>
          <MenuItem
            icon={<MoveIcon color="gray.600" />}
            onClick={onClickMoveFolder}
          >
            Move
          </MenuItem>
          {isDeletable && (
            <MenuItem
              icon={<TrashIcon color="gray.600" />}
              onClick={onClickDeleteFolder}
            >
              Delete
            </MenuItem>
          )}
          <MenuItem icon={<Edit2Icon color="gray.600" />} onClick={onClickEdit}>
            Rename
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
};

type IResourceListFolderProps = {
  folder: ResourceListFolder_LibraryFolderFragment;
  rootFolderId: string;
  navigateToFolder: (folderId: string) => void;
  onAfterMove: () => void;
  currentUserCanEditLibrary: boolean;
  isReorderEnabled?: boolean;
  dragOptions?: {
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
  };
};

export const ResourceListFolder: React.FC<IResourceListFolderProps> & {
  folderFragment: DocumentNode;
} = ({
  folder,
  rootFolderId,
  navigateToFolder,
  onAfterMove,
  currentUserCanEditLibrary,
  isReorderEnabled = false,
  dragOptions,
}) => {
  const renameFolderModal = useDisclosure();
  const deleteFolderModal = useDisclosure();
  const moveFolderModal = useDisclosure();

  if (!folder?.name) return null;

  return (
    <Tr
      ref={dragOptions?.provided.innerRef}
      {...dragOptions?.provided.draggableProps}
      _hover={{ bg: 'gray.50' }}
      minH="4em"
      bg={dragOptions?.snapshot.isDragging ? 'blue.50' : undefined}
    >
      <MoveLibraryFolderModal
        rootFolderId={rootFolderId}
        libraryFolderId={folder._id}
        open={moveFolderModal.isOpen}
        onClose={moveFolderModal.onClose}
        onAfterMove={onAfterMove}
      />

      <DeleteLibraryResourceModal
        onClose={deleteFolderModal.onClose}
        isOpen={deleteFolderModal.isOpen}
        libraryFolderId={folder._id}
      />

      <RenameLibraryResourceModal
        onClose={renameFolderModal.onClose}
        isOpen={renameFolderModal.isOpen}
        libraryFolderId={folder._id}
        initialName={folder.name}
      />
      {currentUserCanEditLibrary && (
        <Td w="8" pr="0">
          <ResourceListDragHandle
            isEnabled={isReorderEnabled}
            dragHandleProps={dragOptions?.provided.dragHandleProps}
          />
        </Td>
      )}
      {currentUserCanEditLibrary && <Td />}
      <Td {...resourceListNameTdProps}>
        <ResourceListName
          icon={<FolderIcon fill="currentcolor" boxSize={6} />}
          onClick={() => navigateToFolder(folder._id)}
          cursor="pointer"
        >
          <Text textStyle="compactStrong" {...resourceListNameTextProps}>
            {folder.name}
          </Text>
        </ResourceListName>
      </Td>
      <Hide below="lg">
        <Td {...resourceListDateTdProps}>
          <Text textStyle="compact">
            {dayjs(folder.dateCreated).format('MMM D, YYYY')}
          </Text>
        </Td>
        <Td />
      </Hide>
      <Td>
        {currentUserCanEditLibrary && (
          <FolderItemDropdown
            isDeletable={folder.isDeletable}
            onClickMoveFolder={moveFolderModal.onOpen}
            onClickDeleteFolder={deleteFolderModal.onOpen}
            onClickEdit={renameFolderModal.onOpen}
          />
        )}
      </Td>
    </Tr>
  );
};

ResourceListFolder.folderFragment = gql`
  fragment ResourceListFolder_LibraryFolderFragment on LibraryFolder {
    _id
    name
    dateCreated
    isDeletable
    position
  }
`;
